import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  model,
  signal,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NgControl, ReactiveFormsModule } from '@angular/forms';
import { ClrBadge, ClrFormsModule } from '@clr/angular';
import { NgHeaderTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { getRandomInt } from 'src/app/misc/functions';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { Key } from 'src/app/types/shared';

@Component({
  selector: 'app-datalist-input',
  template: `
    <ng-select
      class="datalist-input"
      clrControl
      appendTo="body"
      [formControl]="control"
      [items]="showAll() ? items() : filtered()"
      [bindLabel]="labelPath()"
      [bindValue]="valuePath()"
      [placeholder]="placeholder() | mrTranslate"
      [labelForId]="id()"
      [addTag]="insert()"
      [editableSearchTerm]="!clearForSearch()">
      @if (filtered()) {
        <ng-template ng-header-tmp>
          <nav class="sub-nav margin--6">
            <ul class="nav">
              <li class="nav-link" [class.active]="!showAll()" (click)="showAll.set(false)">
                <span class="nav-text">
                  {{ tabsTitles().filtered | mrTranslate }}
                  <clr-badge clrColor="info">{{ filtered().length }}</clr-badge>
                </span>
              </li>
              <li class="nav-link" [class.active]="showAll()" (click)="showAll.set(true)">
                <span class="nav-text">
                  {{ tabsTitles().alle | mrTranslate }}
                  <clr-badge>{{ items().length }}</clr-badge>
                </span>
              </li>
            </ul>
          </nav>
        </ng-template>
      }
    </ng-select>
  `,
  imports: [
    NgSelectComponent,
    NgHeaderTemplateDirective,
    ClrFormsModule,
    ClrBadge,
    FormsModule,
    ReactiveFormsModule,
    MrTranslatePipe,
  ],
  styleUrl: './datalist-input.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatalistInputComponent<T = string> implements ControlValueAccessor {
  private readonly cd = inject(ChangeDetectorRef);
  protected readonly ngControl = inject(NgControl, { self: true });

  readonly datalist = model<T[]>([]);
  readonly labelPath = input<Key<T>>();
  readonly valuePath = input<Key<T>>();
  readonly placeholder = input<string>('Auswählen oder Hinzufügen');
  readonly clearForSearch = input(false);
  readonly insert = input<boolean | ((term: string) => any | Promise<any>)>(true);
  readonly filterFn = input<(row: T) => boolean>(null);
  readonly filteredList = input<T[]>(null);
  readonly tabsTitles = input({ alle: 'Alle', filtered: 'Vorschläge' });

  protected readonly items = computed(() => (this.datalist() ?? []).filter(item => item != null));
  protected readonly filtered = computed(
    () => this.filteredList() ?? (this.filterFn() ? this.items().filter(this.filterFn()) : null)
  );
  protected readonly showAll = linkedSignal(() => this.filtered() === null);

  readonly ngSelect = viewChild.required(NgSelectComponent);
  readonly id = signal<string>(`datalist${getRandomInt(1_000)}`);
  constructor() {
    this.ngControl.valueAccessor = this;
    afterNextRender({
      mixedReadWrite: () => {
        // ng-select needs its bindLabel input before it can map an object value.
        this._isReady = true;
        if (this._hasPendingValue) {
          this.ngSelect().writeValue(this._pendingValue);
        }
        if (this._isDisabled !== undefined) {
          this.ngSelect().setDisabledState(this._isDisabled);
        }
        // Setze das echte ng-select als den finalen ValueAccessor für Validierungen
        this.ngControl.valueAccessor = this.ngSelect();
        // Spiegle die bereits registrierten Pipelines direkt in das ng-select
        this.ngSelect().registerOnChange(this._onChange);
        this.ngSelect().registerOnTouched(this._onTouched);
        this.id.set(this.ngSelect().element.id);
        this.ngSelect().element.removeAttribute('id');
        this.cd.markForCheck();
      },
    });
  }
  writeValue(value: any): void {
    if (this._isReady) {
      this.ngSelect().writeValue(value);
    } else {
      // Angular can write the form value before the child inputs are rendered.
      this._pendingValue = value;
      this._hasPendingValue = true;
    }
  }
  registerOnChange(fn: any): void {
    this._onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    if (this._isReady) {
      this.ngSelect().setDisabledState(isDisabled);
    } else {
      // Sichern, falls ng-select noch nicht gerendert ist
      this._isDisabled = isDisabled;
    }
  }

  // Cache-Variablen für das Timing-Problem
  private _isReady = false;
  private _hasPendingValue = false;
  private _pendingValue: any;
  private _isDisabled: boolean;
  private _onChange: (_: any) => void = () => {};
  private _onTouched: () => void = () => {};

  // In your component.ts
  get control(): FormControl {
    return this.ngControl.control as FormControl;
  }
}
